<div class="">
  <div class="max-content-width spaced-sections">
    <!-- Info & Advance Stage PI Action -->
    <div
      class="flex justify-between items-center bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
    >
      <div>
        <h2 class="text-sm font-bold text-slate-800">Verify PDFs</h2>
        <p class="text-xs text-slate-500 mt-0.5">
          Verify that correct PDFs exist for all papers passing first-stage screening.
        </p>
      </div>
      @if (isProjectPI()) {
        <button
          type="button"
          [disabled]="!canAdvance() || submitting"
          (click)="advanceConfirmOpen = true"
          [class.btn-disabled]="!canAdvance()"
          class="btn btn-advance flex items-center gap-2"
        >
          @if (submitting) {
            <span class="spinner-border spinner-border-sm" role="status"></span>
            Advancing...
          } @else {
            Advance to Full-Text Screening
          }
        </button>
      }
    </div>

    <!-- Main View Router Container -->
    @if (loading) {
      <div class="loading-container py-12 flex flex-col items-center justify-center">
        <div class="spinner-border text-primary mb-4" role="status"></div>
        <p class="text-slate-600">Loading workspace queue...</p>
      </div>
    } @else if (!selectedPaper) {
      <!-- VIEW A: Dashboard Overview -->
      <div class="dashboard-grid mb-6">
        <!-- Progress Indicator -->
        <div class="dashboard-card progress-card">
          <div class="card-label">Retrieval Progress</div>
          <div class="progress-details flex justify-between items-baseline mb-2">
            <span class="progress-number font-bold text-2xl"
              >{{ resolvedCount }} / {{ totalProjectPapers }}</span
            >
            <span class="progress-label text-slate-500 text-xs">Papers Finalized</span>
          </div>
          <div class="progress-track w-full bg-slate-200 h-2 rounded-full overflow-hidden">
            <div
              class="progress-fill h-full rounded-full"
              [style.width.%]="progressPercentage"
            ></div>
          </div>
        </div>

        <!-- Quick Metrics -->
        <div class="dashboard-card metrics-card flex flex-row justify-around items-center p-4">
          <div class="metric-item text-center w-28">
            <span class="block text-2xl font-bold text-slate-800">{{ totalProjectPapers }}</span>
            <span class="text-xs text-slate-500 uppercase tracking-wider">Total</span>
          </div>
          <div class="border-l border-slate-300 h-10"></div>
          <div class="metric-item text-center w-28">
            <span class="block text-2xl font-bold text-emerald-600">{{ totalVerifiedPapers }}</span>
            <span class="text-xs text-slate-500 uppercase tracking-wider">Verified</span>
          </div>
          <div class="border-l border-slate-300 h-10"></div>
          <div class="metric-item text-center w-28">
            <span class="block text-2xl font-bold text-rose-600">{{ totalUnavailablePapers }}</span>
            <span class="text-xs text-slate-500 uppercase tracking-wider">Unavailable</span>
          </div>
        </div>
      </div>

      <!-- Filter & Search Controls -->
      <div
        class="filter-controls-card p-4 border border-slate-200 rounded-xl mb-6 bg-white flex flex-wrap items-center justify-between gap-4"
      >
        <div class="flex-1 min-w-[260px]">
          <div class="relative search-input-wrapper">
            <input
              type="text"
              [(ngModel)]="searchText"
              (ngModelChange)="applyFilters()"
              placeholder="Search papers by Title, Authors, or DOI..."
              class="input-field w-full pl-10"
            />
          </div>
        </div>

        <div class="flex items-center gap-3">
          <span class="text-xs font-semibold text-slate-500 uppercase">Status:</span>
          <select [(ngModel)]="statusFilter" (change)="applyFilters()" class="select-field">
            <option value="ALL">All Papers</option>
            <option value="PENDING">Pending Download</option>
            <option value="DOWNLOADING">Downloading...</option>
            <option value="FAILED">Download Failed</option>
            <option value="MISSING">Missing / No PDF</option>
            <option value="AWAITING_VERIFICATION">Awaiting Verification</option>
            <option value="VERIFIED">Verified</option>
            <option value="UNAVAILABLE">Unavailable</option>
          </select>
        </div>
      </div>

      <!-- Papers Dashboard Table -->
      @if (filteredPapers.length === 0) {
        <div class="empty-state-card text-center py-12 bg-white border border-slate-200 rounded-xl">
          <p class="text-slate-500">No papers found matching the selected filters.</p>
        </div>
      } @else {
        <div class="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div class="overflow-x-auto">
            <table class="min-w-full table-auto text-sm text-left">
              <thead>
                <tr
                  class="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-xs"
                >
                  <th class="py-3 px-6">Title & Authors</th>
                  <th class="py-3 px-4">DOI</th>
                  <th class="py-3 px-4 text-center">Status</th>
                  <th class="py-3 px-4">Workspace Lock</th>
                  <th class="py-3 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (paper of filteredPapers; track paper.id) {
                  <tr class="hover:bg-slate-50 transition-colors">
                    <!-- Title & Authors -->
                    <td class="py-4 px-6 align-middle max-w-md">
                      <div class="font-semibold text-slate-900 line-clamp-2">{{ paper.title }}</div>
                      <div
                        class="text-xs text-slate-500 mt-1 font-medium truncate"
                        [title]="paper.authors"
                      >
                        {{ paper.authors }}
                      </div>
                    </td>

                    <!-- DOI -->
                    <td class="py-4 px-4 align-middle text-slate-600 font-mono text-xs">
                      @if (paper.doi) {
                        <a
                          [href]="doiUrl(paper.doi)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {{ cleanDoi(paper.doi) }}
                        </a>
                      } @else {
                        N/A
                      }
                    </td>

                    <!-- Status -->
                    <td class="py-4 px-4 align-middle text-center">
                      <span [class]="getStatusClass(paper.status)">
                        {{ formatStatus(paper.status) }}
                      </span>
                    </td>

                    <!-- Lock -->
                    <td class="py-4 px-4 align-middle">
                      @if (paper.lockedByUserName) {
                        <span
                          class="lock-badge flex items-center gap-1.5 text-amber-800 bg-amber-50 border border-amber-200 rounded-full py-0.5 px-2.5 text-xs font-semibold w-fit"
                        >
                          Working: {{ paper.lockedByUserName }}
                        </span>
                      } @else {
                        <span class="text-slate-400 text-xs font-medium">Free</span>
                      }
                    </td>

                    <!-- Actions -->
                    <td class="py-4 px-6 align-middle text-right">
                      <div class="flex items-center justify-end gap-2">
                        @if (isCorrectStage()) {
                          @if (paper.status === 'VERIFIED' || paper.status === 'UNAVAILABLE') {
                            <button
                              type="button"
                              (click)="undoDecision(paper)"
                              class="btn-small-secondary flex items-center gap-1"
                            >
                              ↩ Undo
                            </button>
                          } @else {
                            @if (paper.status === 'FAILED' && isProjectPI()) {
                              <button
                                type="button"
                                (click)="retryDownload(paper)"
                                class="btn-small-secondary flex items-center gap-1"
                              >
                                ↩ Retry Download
                              </button>
                            }
                            <button
                              type="button"
                              [disabled]="
                                paper.lockedByUserName &&
                                paper.lockedByUserName !== getCurrentUserFullName()
                              "
                              (click)="openWorkspace(paper)"
                              class="btn-small-primary"
                            >
                              Open Workspace
                            </button>
                          }
                        } @else {
                          <button
                            type="button"
                            (click)="openWorkspace(paper)"
                            class="btn-small-secondary flex items-center gap-1"
                          >
                            👁️ View
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!-- Pagination footer -->
          <div
            class="flex flex-col items-center justify-between gap-4 border-t border-slate-200 px-6 py-4 text-sm text-slate-600 sm:flex-row sm:items-center"
            data-testid="paper-list-pagination"
          >
            <div class="flex items-center font-medium">
              Page {{ currentPageIndex + 1 }} of {{ totalPages || 1 }} · {{ totalElements }} total
            </div>
            <div class="flex items-center gap-6">
              <!-- Page-size dropdown -->
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 font-medium">Per page:</span>
                <select
                  [ngModel]="currentPageSize"
                  (ngModelChange)="onPageSizeChange($event)"
                  class="px-2 py-1 border border-slate-200 rounded-lg text-xs text-slate-700 bg-white hover:border-slate-300 focus:outline-none cursor-pointer font-medium transition-colors"
                >
                  @for (size of pageSizeOptions; track size) {
                    <option [value]="size">{{ size }}</option>
                  }
                </select>
              </div>

              <!-- Paging buttons -->
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  (click)="goPrev()"
                  [disabled]="currentPageIndex === 0"
                  class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  « Prev
                </button>
                <button
                  type="button"
                  (click)="goNext()"
                  [disabled]="currentPageIndex >= totalPages - 1"
                  class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next »
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    } @else {
      <!-- VIEW B: Split-Screen Verification Workspace -->
      <div
        class="split-workspace-container grid grid-cols-1 lg:grid-cols-12 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-200"
      >
        <!-- Left side: Metadata and Actions -->
        <div class="lg:col-span-4 flex flex-col gap-4">
          <div class="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div class="flex justify-between items-start mb-3">
              <span [class]="getStatusClass(selectedPaper.status)">
                {{ formatStatus(selectedPaper.status) }}
              </span>
              <button
                type="button"
                (click)="closeWorkspace()"
                class="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕ Close
              </button>
            </div>

            <h2 class="text-xl font-bold text-slate-900 mb-3 leading-snug">
              {{ selectedPaper.title }}
            </h2>

            <div class="space-y-3 text-sm text-slate-700 border-t border-slate-100 pt-3">
              <div>
                <span class="block text-xs font-semibold uppercase text-slate-400">Authors</span>
                <span
                  class="font-medium text-slate-800 line-clamp-3"
                  [title]="selectedPaper.authors"
                  >{{ selectedPaper.authors || 'N/A' }}</span
                >
              </div>
              @if (selectedPaper.doi) {
                <div>
                  <span class="block text-xs font-semibold uppercase text-slate-400">DOI</span>
                  <a
                    [href]="doiUrl(selectedPaper.doi)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {{ cleanDoi(selectedPaper.doi) }}
                  </a>
                </div>
              }
              @if (selectedPaper.year) {
                <div>
                  <span class="block text-xs font-semibold uppercase text-slate-400">Year</span>
                  <span class="font-medium text-slate-800">{{ selectedPaper.year }}</span>
                </div>
              }
              <div>
                <span class="block text-xs font-semibold uppercase text-slate-400">Source</span>
                <span class="font-medium text-slate-800">{{ selectedPaper.source }}</span>
              </div>
            </div>
          </div>

          <!-- Sidebar Action buttons -->
          <div class="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
            <h3 class="text-xs font-bold uppercase text-slate-400 tracking-wider mb-2">
              Finalize Resolution
            </h3>

            <button
              type="button"
              [disabled]="!pdfUrl || !isCorrectStage()"
              (click)="verifyPaper()"
              [class.btn-disabled]="!pdfUrl || !isCorrectStage()"
              class="btn btn-verify w-full flex items-center justify-center gap-2 font-bold py-2.5 rounded-lg text-white"
            >
              ✓ Verify PDF is Correct
            </button>

            <button
              type="button"
              [disabled]="!isCorrectStage()"
              (click)="markUnavailable()"
              [class.btn-disabled]="!isCorrectStage()"
              class="btn btn-mark-unavailable w-full flex items-center justify-center gap-2 font-semibold py-2.5 rounded-lg border text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100"
            >
              ✕ Mark Full-Text Unavailable
            </button>

            <button
              type="button"
              (click)="closeWorkspace()"
              class="btn w-full flex items-center justify-center gap-2 font-semibold py-2.5 rounded-lg border bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        <!-- Right side: PDF Viewer or Uploader -->
        <div
          class="lg:col-span-8 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col min-h-[500px]"
        >
          @if (uploadingPdf) {
            <div
              class="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-50/50"
            >
              <div class="spinner-border text-primary mb-4" role="status"></div>
              <h3 class="font-bold text-slate-800">Processing Upload</h3>
              <p class="text-slate-500 text-sm mt-1">
                Uploading PDF and executing metadata matching...
              </p>
            </div>
          } @else if (loadingPdf) {
            <div
              class="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-50/50"
            >
              <div class="spinner-border text-primary mb-4" role="status"></div>
              <h3 class="font-bold text-slate-800">Loading PDF Document</h3>
              <p class="text-slate-500 text-sm mt-1">
                Fetching the PDF safely from the repository...
              </p>
            </div>
          } @else if (pdfUrl) {
            <!-- Embedded PDF Viewer -->
            <div class="flex-1 flex flex-col h-full relative">
              <ngx-extended-pdf-viewer
                [src]="pdfUrl"
                [showToolbar]="true"
                [textLayer]="true"
                [showSidebarButton]="false"
                [sidebarVisible]="false"
                [showOpenFileButton]="false"
                [showDownloadButton]="false"
                height="600px"
              >
              </ngx-extended-pdf-viewer>
              @if (isCorrectStage()) {
                <div
                  class="p-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-xs"
                >
                  <span class="text-slate-500"
                    >Need to update the document? Drag & drop a new PDF over this zone to replace
                    it.</span
                  >
                  <label class="font-semibold text-blue-600 hover:text-blue-800 cursor-pointer">
                    Replace PDF
                    <input
                      type="file"
                      (change)="onFilePick($event)"
                      accept="application/pdf"
                      class="hidden"
                    />
                  </label>
                </div>
              }
            </div>
          } @else {
            @if (isCorrectStage()) {
              <!-- Drop Zone Uploader -->
              <div
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onFileDrop($event)"
                [class.drag-over]="dragOver"
                class="flex-1 flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-300 hover:border-slate-400 rounded-xl m-6 transition-all drag-drop-zone text-center bg-slate-50/30"
              >
                <div class="text-5xl mb-4">📄</div>
                <h3 class="text-lg font-bold text-slate-800">Add Full-Text PDF</h3>
                <p class="text-slate-500 text-sm mt-1 max-w-sm">
                  Drag & drop the paper's PDF file directly into this zone, or browse to upload.
                </p>
                <label
                  class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 cursor-pointer shadow-sm"
                >
                  Browse PDF
                  <input
                    type="file"
                    (change)="onFilePick($event)"
                    accept="application/pdf"
                    class="hidden"
                  />
                </label>
                <p class="text-slate-400 text-xs mt-3">
                  Maximum file size: 100 MB. PDF format only.
                </p>
              </div>
            } @else {
              <!-- Empty State in read-only subsequent stages -->
              <div
                class="flex-1 flex flex-col items-center justify-center p-12 text-center bg-slate-50/50"
              >
                <div class="text-5xl mb-4">⚠️</div>
                <h3 class="text-lg font-bold text-slate-700">No PDF Available</h3>
                <p class="text-slate-500 text-sm mt-1 max-w-sm">
                  This paper does not have a PDF document retrieved. Stage-modifying actions are
                  disabled.
                </p>
              </div>
            }
          }
        </div>
      </div>
    }
  </div>
</div>

<app-confirm-modal
  [(open)]="advanceConfirmOpen"
  [title]="'Advance to Full-Text Screening'"
  [message]="'Are you sure you want to advance this project to the Full-Text Screening phase? This will close the PDF verification phase and lock all decisions. This action cannot be undone.'"
  (confirmed)="advanceProject()"
  (canceled)="advanceConfirmOpen = false"
></app-confirm-modal>
